// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  AddInvoiceFormData,
  addInvoiceFormSchema,
} from '@/billing/schemas/add-invoice-form-schema';
import { isCustomPaymentTerm } from '@/billing/utilities/custom-payment-term';
import { FormDatePicker } from '@/components/form-date-picker';
import { FormTextField } from '@/components/form-text-field';
import { Customer } from '@/customer/api/types';
import { CustomerAutocomplete } from '@/customer/components/customer-autocomplete';
import {
  flourishFieldSx,
  useLinkedFieldFlourish,
} from '@/hooks/use-linked-field-flourish';
import { PaymentTerm } from '@/payment-term/api/types';
import { PaymentTermAutocomplete } from '@/payment-term/components/payment-term-autocomplete';
import { ProjectAutocomplete } from '@/project/components/project-autocomplete';
import { useSparseProjectList } from '@/project/hooks/use-sparse-project-list';
import { SparseProject } from '@/sparse/api/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Stack, useTheme } from '@mui/material';
import { DateTime } from 'luxon';
import { Suspense, useCallback, useEffect, useMemo, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';

interface AddInvoiceFormProps {
  customers: Customer[];
  paymentTermOptions: PaymentTerm[];
  orgDefaultPaymentTerm: PaymentTerm | null;
  formId: string;
  isDisabled: boolean;
  isOpen: boolean;
  onSubmit: (data: AddInvoiceFormData) => void;
}

const SELECTED_CUSTOMER_GROUP = 'Selected customer';
const OTHER_PROJECTS_GROUP = 'Other projects';
const NO_SELECTED_CUSTOMER_GROUP = 'All projects';

// Returns invoiceDate + term.netDays, or null when the term is null, a
// custom-label sentinel, or has no netDays. Centralizes the
// term-applies-to-due-date rule used at default-seed time and on every
// term/invoice-date change.
function computeDueDateFromTerm(
  invoiceDate: DateTime,
  term: PaymentTerm | null
): DateTime | null {
  if (term === null) return null;
  if (isCustomPaymentTerm(term)) return null;
  if (term.netDays === null) return null;
  return invoiceDate.plus({ days: term.netDays });
}

function getDefaultValues(
  orgDefaultPaymentTerm: PaymentTerm | null
): AddInvoiceFormData {
  const today = DateTime.now().startOf('day');
  const seededDueDate = computeDueDateFromTerm(today, orgDefaultPaymentTerm);
  return {
    customer: null,
    project: null,
    invoiceDate: today,
    dueDate: seededDueDate,
    paymentTerm: seededDueDate !== null ? orgDefaultPaymentTerm : null,
    purchaseOrderNumber: '',
  };
}

export function AddInvoiceForm(props: AddInvoiceFormProps) {
  const theme = useTheme();
  const { trigger: triggerFlourish, isActive: isFlourishActive } =
    useLinkedFieldFlourish();
  const { control, handleSubmit, watch, setValue, reset, getValues } =
    useForm<AddInvoiceFormData>({
      resolver: zodResolver(addInvoiceFormSchema),
      defaultValues: getDefaultValues(props.orgDefaultPaymentTerm),
    });

  const wasOpenRef = useRef(props.isOpen);
  useEffect(() => {
    if (props.isOpen && !wasOpenRef.current) {
      reset(getDefaultValues(props.orgDefaultPaymentTerm));
    }
    wasOpenRef.current = props.isOpen;
  }, [props.isOpen, props.orgDefaultPaymentTerm, reset]);

  const customer = watch('customer');
  const project = watch('project');

  // Org-wide sparse project list. The selected customer drives priority +
  // group ordering rather than filtering, so the user can pick a project
  // first and the form will resolve the customer from the project.
  const projectsQuery = useSparseProjectList();
  const projectOptions = useMemo<SparseProject[]>(() => {
    const all = projectsQuery.projects;
    if (customer === null) {
      return all;
    }
    const selected: SparseProject[] = [];
    const others: SparseProject[] = [];
    for (const candidate of all) {
      if (candidate.customer?.id === customer.id) {
        selected.push(candidate);
      } else {
        others.push(candidate);
      }
    }
    return [...selected, ...others];
  }, [projectsQuery.projects, customer]);

  const groupBy = useCallback(
    (option: SparseProject) => {
      if (customer === null) return NO_SELECTED_CUSTOMER_GROUP;
      return option.customer?.id === customer.id
        ? SELECTED_CUSTOMER_GROUP
        : OTHER_PROJECTS_GROUP;
    },
    [customer]
  );

  // When the user picks a project, seed the customer + billing defaults.
  // Customer auto-fill is one-way: only seeds when customer is currently
  // null and the project carries one. A non-null customer is never
  // overwritten — the user chose it deliberately. If the project has no
  // customer the user must pick one before submit (schema rejects null).
  const lastProjectIdRef = useRef<string | null>(null);
  useEffect(() => {
    const nextId = project?.id ?? null;
    if (nextId === lastProjectIdRef.current) return;
    lastProjectIdRef.current = nextId;

    if (project === null) {
      setValue('paymentTerm', null);
      setValue('purchaseOrderNumber', '');
      return;
    }

    if (customer === null && project.customer !== null) {
      setValue('customer', project.customer);
    }

    const projectTerm =
      project.paymentTermLabel === null
        ? null
        : (props.paymentTermOptions.find(
            (t) => t.label === project.paymentTermLabel
          ) ?? null);
    const effectiveTerm: PaymentTerm | null =
      projectTerm ?? props.orgDefaultPaymentTerm;
    setValue('paymentTerm', effectiveTerm);
    setValue('purchaseOrderNumber', project.purchaseOrderNumber ?? '');
    const invoiceDate = getValues('invoiceDate');
    if (invoiceDate instanceof DateTime && invoiceDate.isValid) {
      const newDueDate = computeDueDateFromTerm(invoiceDate, effectiveTerm);
      if (newDueDate !== null) {
        setValue('dueDate', newDueDate, {
          shouldDirty: true,
          shouldValidate: true,
        });
        triggerFlourish('dueDate');
      }
    }
  }, [
    project,
    customer,
    props.orgDefaultPaymentTerm,
    props.paymentTermOptions,
    setValue,
    getValues,
    triggerFlourish,
  ]);

  const poFieldDisabled = props.isDisabled || project === null;

  const handlePaymentTermChange = useCallback(
    (
      term: PaymentTerm | null,
      fieldOnChange: (value: PaymentTerm | null) => void
    ) => {
      fieldOnChange(term);
      const invoiceDate = getValues('invoiceDate');
      if (!(invoiceDate instanceof DateTime) || !invoiceDate.isValid) return;
      const newDueDate = computeDueDateFromTerm(invoiceDate, term);
      if (newDueDate === null) return;
      setValue('dueDate', newDueDate, {
        shouldDirty: true,
        shouldValidate: true,
      });
      triggerFlourish('dueDate');
    },
    [triggerFlourish, getValues, setValue]
  );

  const handleInvoiceDateChange = useCallback(
    (next: DateTime | null) => {
      if (next === null || !next.isValid) return;
      const term = getValues('paymentTerm');
      const newDueDate = computeDueDateFromTerm(next, term);
      if (newDueDate === null) return;
      setValue('dueDate', newDueDate, {
        shouldDirty: true,
        shouldValidate: true,
      });
      triggerFlourish('dueDate');
    },
    [triggerFlourish, getValues, setValue]
  );

  const dueDateSx = flourishFieldSx(theme, isFlourishActive('dueDate'));

  return (
    <form id={props.formId} onSubmit={handleSubmit(props.onSubmit)} noValidate>
      <Stack spacing={3}>
        <Controller
          name="customer"
          control={control}
          render={({ field, fieldState }) => (
            <CustomerAutocomplete
              size="small"
              options={props.customers}
              value={field.value}
              onChange={(_event, value) => field.onChange(value)}
              disabled={props.isDisabled}
              label="Customer"
              placeholder="Select a customer"
              error={fieldState.invalid}
              helperText={fieldState.error?.message}
            />
          )}
        />
        <Controller
          name="project"
          control={control}
          render={({ field, fieldState }) => (
            <ProjectAutocomplete<SparseProject, false>
              size="small"
              label="Project"
              placeholder="Select a project"
              value={field.value}
              onChange={(_event, value) => field.onChange(value)}
              options={projectOptions}
              disabled={props.isDisabled || projectsQuery.isLoading}
              error={fieldState.invalid}
              helperText={fieldState.error?.message}
              groupBy={groupBy}
              showStatusChip
            />
          )}
        />
        <Suspense fallback={null}>
          <FormDatePicker
            control={control}
            name="invoiceDate"
            label="Invoice date"
            isDisabled={props.isDisabled}
            onChangeComplete={handleInvoiceDateChange}
          />
        </Suspense>
        <Controller
          name="paymentTerm"
          control={control}
          render={({ field, fieldState }) => (
            <PaymentTermAutocomplete
              size="small"
              label="Payment terms"
              options={props.paymentTermOptions}
              value={field.value}
              onChange={(_event, value) =>
                handlePaymentTermChange(value, field.onChange)
              }
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message}
              disabled={props.isDisabled}
            />
          )}
        />
        <Box sx={dueDateSx}>
          <Suspense fallback={null}>
            <FormDatePicker
              control={control}
              name="dueDate"
              label="Due date"
              isDisabled={props.isDisabled}
            />
          </Suspense>
        </Box>
        <FormTextField
          name="purchaseOrderNumber"
          control={control}
          label="PO number"
          disabled={poFieldDisabled}
          fullWidth
        />
      </Stack>
    </form>
  );
}
